'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Lightweight hook to track the width and height of a container element.
 * Attach the returned `containerRef` to the element you want to measure.
 */
export function useContainerWidth() {
    const [containerWidth, setContainerWidth] = useState<number>(0)
    const [containerHeight, setContainerHeight] = useState<number>(0)
    const nodeRef = useRef<HTMLDivElement | null>(null)
    const observerRef = useRef<ResizeObserver | null>(null)
    const rafIdRef = useRef<number | null>(null)

    // Callback ref ensures observer attaches exactly when element mounts/changes
    const setContainerRef = useCallback((node: HTMLDivElement | null) => {
        // Disconnect any previous observer and cancel pending RAF
        if (observerRef.current) {
            observerRef.current.disconnect()
            observerRef.current = null
        }
        if (rafIdRef.current !== null) {
            cancelAnimationFrame(rafIdRef.current)
            rafIdRef.current = null
        }

        nodeRef.current = node

        if (!node) return

        // Observe the element for intrinsic size changes (not just window resizes)
        // ResizeObserver will fire immediately with initial dimensions, avoiding forced reflow
        observerRef.current = new ResizeObserver((entries) => {
            const entry = entries[0]
            if (!entry) return

            // Use borderBoxSize for more accurate measurements if available
            const width = entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect?.width
            const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect?.height

            if (typeof width === 'number' && width > 0) {
                setContainerWidth(width)
            }
            if (typeof height === 'number' && height > 0) {
                setContainerHeight(height)
            }
        })

        observerRef.current.observe(node, { box: 'border-box' })
    }, []) // No dependencies - stable callback ref

    // Fallback: also update on window resize (covers viewport changes)
    // Use requestAnimationFrame to avoid forced reflow
    useEffect(() => {
        const handleResize = () => {
            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current)
            }
            rafIdRef.current = requestAnimationFrame(() => {
                if (nodeRef.current) {
                    setContainerWidth(nodeRef.current.offsetWidth)
                    setContainerHeight(nodeRef.current.offsetHeight)
                }
                rafIdRef.current = null
            })
        }
        window.addEventListener('resize', handleResize)
        return () => {
            window.removeEventListener('resize', handleResize)
            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current)
            }
        }
    }, [])

    // Cleanup observer and RAF on unmount
    useEffect(() => {
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect()
                observerRef.current = null
            }
            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current)
                rafIdRef.current = null
            }
        }
    }, [])

    return {
        containerWidth,
        containerHeight,
        containerRef: setContainerRef,
    }
}
